import type { ComponentProps } from 'react';
import React, { memo } from 'react';
import styled from '@emotion/styled';
import { alignItems, display, flexWrap, justifyContent, space } from 'styled-system';

import { listStyle } from 'common/theme/system-utilities';
import LinkSectionListItem from './LinkSectionListItem';
import type { LinkSectionItem } from './types';

interface LinkSectionListProps {
  items: LinkSectionItem[];
  isOpen: boolean;
  isInline?: boolean;
}

const UnstyledUl = styled.ul<any>`
  ${display};
  ${space};
  ${flexWrap};
  ${listStyle};
`;

const Ul = (props: ComponentProps<typeof UnstyledUl>) => (
  <UnstyledUl
    px="sp_0"
    pt={['sp_16', null, 'sp_0']}
    pb={['sp_24', null, 'sp_24']}
    m="sp_0"
    mr={['-16px', '-24px', null, '-32px']}
    flexWrap="wrap"
    listStyle="none"
    {...props}
  />
);

const UnstyledUlInline = styled(Ul)<any>`
  ${alignItems};
  ${justifyContent};
`;

const UlInline = (props: ComponentProps<typeof UnstyledUlInline>) => (
  <UnstyledUlInline
    mr={['-16px', '-24px', null, '-48px']}
    pb="sp_16"
    alignItems="center"
    justifyContent="center"
    {...props}
  />
);

const LinkSectionList = ({ items, isOpen, isInline = false }: LinkSectionListProps) => {
  const isAnyImage = items.some(item => 'image' in item && !!item.image);
  const List = isInline ? UlInline : Ul;

  return (
    <List
      display={[isOpen ? 'flex' : 'none', null, 'flex']}
      /** We are adjusting the column margin to match the items offsets */
      data-testid="link-section-list"
      {...(isAnyImage
        ? {
            mr: '-16px',
          }
        : {})}
    >
      {items.map((item, index) => (
        <LinkSectionListItem key={index} index={index} item={item} isInline={isInline} />
      ))}
    </List>
  );
};

export default memo(LinkSectionList);
